import React, {
  createContext,
  useContext,
  useTransition,
  useCallback,
  useMemo,
} from 'react';
import { useHistory } from 'react-router-dom';

interface TransitionContextValue {
  isPending: boolean;
  navigate: (to: string) => void;
  navigateWithTransition: (to: string) => void;
}

const TransitionContext = createContext<TransitionContextValue | null>(null);

export const TransitionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const history = useHistory();
  const [isPending, startTransition] = useTransition();

  // Note: Global loading state is managed by ProtectedApp/ProtectedRoute
  // based on actual data loading, not transition pending state

  const navigate = useCallback(
    (to: string) => {
      history.push(to);
    },
    [history],
  );

  const navigateWithTransition = useCallback(
    (to: string) => {
      startTransition(() => {
        history.push(to);
      });
    },
    [history, startTransition],
  );

  const value = useMemo(
    () => ({
      isPending,
      navigate,
      navigateWithTransition,
    }),
    [isPending, navigate, navigateWithTransition],
  );

  return (
    <TransitionContext.Provider value={value}>
      {children}
    </TransitionContext.Provider>
  );
};

export const useTransitionNavigation = () => {
  const context = useContext(TransitionContext);
  if (!context) {
    throw new Error(
      'useTransitionNavigation must be used within a TransitionProvider',
    );
  }
  return context;
};
